/* eslint-disable no-param-reassign */
import { useCallback, useMemo } from 'react';

import getRequestMeta from '../utils/functions/generateMeta';

import { ip } from '../utils/functions/urls';
import { error as errorLabels } from '../label';

const getToken = async (u) => u.getIdToken();

function useKpiData(currentUser) {
  /**
   * [Backend-request]
   *
   * Generates a temporary kpi data
   * @param {object} kpiMeta Kpi meta data
   * @callback genKpiData
   */
  const genKpiData = useCallback(async (kpiMeta, database) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'POST', 'JSON'),
        body: JSON.stringify({
          ...kpiMeta,
          file_id: database,
        }),
      };
      const res = await fetch(`${ip}/kpis/create`, opt);
      const json = await res.json();
      if (res.status !== 200) {
        return { error: true, msg: errorLabels.useKpiData.genKpiData, raw: json };
      }
      return { error: false, data: json.info, errorMsg: json.error };
    } catch (er) {
      return { error: true, msg: errorLabels.generic, raw: `Erro do sistema: ${er.toString()}` };
    }
  }, [currentUser]);

  const genSubsetData = useCallback(async (kpiMeta, database, page = 0, sortBy = '', sortOrder = 'asc') => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'POST', 'JSON'),
        body: JSON.stringify({
          ...kpiMeta,
          file_id: database,
          page: page + 1,
          sortBy,
          sortOrder,
        }),
      };
      const res = await fetch(`${ip}/kpis/subset`, opt);
      const json = await res.json();
      if (res.status !== 200) {
        return { error: true, msg: errorLabels.useKpiData.genSubsetData, raw: json };
      }
      return { error: false, data: json.info };
    } catch (er) {
      return { error: true, msg: errorLabels.generic, raw: `Erro do sistema: ${er.toString()}` };
    }
  }, [currentUser]);

  const genReportGridData = useCallback(async (configuration, signal) => {
    try {
      const token = await getToken(currentUser);

      const options = {
        ...await getRequestMeta(token, 'POST', 'JSON'),
        signal,
        body: JSON.stringify(configuration),
      };

      const response = await fetch(
        `${ip}/kpis/report-grid`,
        options,
      );

      const json = await response.json();

      if (!response.ok) {
        return {
          error: true,
          msg: json.msg || 'Não foi possível gerar o relatório.',
          raw: json,
        };
      }

      return {
        error: false,
        data: json.info,
      };
    } catch (failure) {
      // Preserva o cancelamento feito pelo editor.
      if (signal?.aborted || failure.name === 'AbortError') {
        throw failure;
      }

      return {
        error: true,
        msg: errorLabels.generic,
        raw: failure.toString(),
      };
    }
  }, [currentUser]);

  const getReportGridDetails = useCallback(async (payload, signal) => {
    const token = await getToken(currentUser);

    const response = await fetch(`${ip}/kpis/report-grid/details`, {
      ...await getRequestMeta(token, 'POST', 'JSON'),
      signal,
      body: JSON.stringify(payload),
    });

    const json = await response.json();

    if (!response.ok) {
      throw new Error(json.msg || 'Não foi possível carregar os detalhes.');
    }

    return json.info;
  }, [currentUser]);

  const genMultData = useCallback(async (kpiData = {}, database) => {
    try {
      const token = await getToken(currentUser);
      const opt = {
        ...await getRequestMeta(token, 'POST', 'JSON'),
        body: JSON.stringify({
          fileId: database,
          items: Object.values(kpiData).map((kpiDt) => {
            const aux = {
              ...kpiDt.body,
              kpiId: kpiDt.id,
              kpiType: kpiDt.type,
            };
            if (kpiDt.type === 'SubsetTable') {
              aux.page = (kpiDt.extra.page ?? 1) + 1;
              aux.sortBy = kpiDt.extra.sortBy ?? '';
              aux.sortOrder = kpiDt.extra.sortOrder ?? 'asc';
            } else if (kpiDt.type === 'smartLabel') {
              aux.code = kpiDt.extra.code ?? '';
              aux.revision = kpiDt.extra.revision ?? 1;
            }
            return aux;
          }),
        }),
      };
      const res = await fetch(`${ip}/kpis/multi`, opt);
      const json = await res.json();
      if (res.status !== 200) {
        return { error: true, msg: errorLabels.useKpiData.genMultData, raw: json };
      }
      return { error: false, kpis: json.info };
    } catch (er) {
      return { error: true, msg: errorLabels.generic, raw: `Erro do sistema: ${er.toString()}` };
    }
  }, [currentUser]);

  const middleware = useCallback(async (action) => {
    switch (action.type) {
      case 'genKpiData': {
        const getRes = await genKpiData(action.kpiMeta, action.database);
        // if (getRes.error) return getRes;
        return getRes;
      }
      case 'genSubsetData': {
        const getRes = await genSubsetData(
          action.kpiMeta, action.database, action.page, action.sortBy, action.sortOrder,
        );
        if (getRes.error) return getRes;
        return getRes;
      }
      case 'genReportGridData': {
        return genReportGridData(
          action.configuration,
          action.signal,
        );
      }
      case 'genMultData': {
        const getRes = await genMultData(action.kpiData, action.database);
        if (getRes.error) return getRes;
        Object.entries(action.kpiData).forEach(([k, dt]) => {
          const datum = getRes.kpis?.[k] ?? null;
          if (datum) {
            if (dt.type === 'ReportGrid') {
              dt.callback({
                dataset: datum.data,
                error: datum.error,
              });
            } else if (dt.type === 'Content') {
              dt.callback((it) => ({ ...it, name: datum.data.data?.[0]?.[0] ?? '-' }));
            } else if (dt.type === 'smartLabel') {
              dt.callback((it) => ({
                ...it,
                name: datum.data ?? '-',
                meta: { error: datum.error?.split('|')?.[0] ?? null },
              }));
            } else {
              dt.callback((it) => ({
                ...it,
                data: datum.data,
                meta: {
                  ...it.meta,
                  error: datum.error,
                },
              }));
            }
          }
        });
        return getRes.data;
      }
      default:
        return {
          error: false, msg: 'DEFAULT', raw: {}, default: true,
        };
    }
  }, [genKpiData, genSubsetData, genReportGridData, genMultData]);

  const kpiAPI = useMemo(() => ({
    genKpiData: async (kpiMeta, database) => middleware({
      type: 'genKpiData', kpiMeta, database,
    }),
    genSubsetData: async (kpiMeta, database, page, sortBy, sortOrder) => middleware({
      type: 'genSubsetData', kpiMeta, database, page, sortBy, sortOrder,
    }),    
    genMultData: async (kpiData, database) => middleware({
      type: 'genMultData', kpiData, database,
    }),
    genReportGridData: async (configuration, signal) => middleware({
      type: 'genReportGridData',
      configuration,
      signal,
    }),
    getReportGridDetails: async (payload, signal) => getReportGridDetails(payload, signal),

  }), [middleware, getReportGridDetails]);

  return [kpiAPI];
}

export default useKpiData;
